import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import { FormProvider, useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { store } from 'src/app/store';
import { setIsDirty } from 'src/app/reducers/manage-room.reducer';
import { transformPermissionFields, getWhoCanJoinRoomValue, displayErrors } from '../../helpers';
import { UnSavedWarnModal } from '../unsaved-warn-modal';
import { UpdateRoomConfirmModal } from '../update-room-confirm-modal';
import { useUpdateRoomMutation } from '../../manage-room.slice';
import { DiscardChangesModal } from '../discard-changes-modal';
import { GeneralSection, RoomSettingsSection } from './components';
import { GeneralSettingsProps, UpdatedRoomPayload } from './general-settings.types';
import { updateRoomValidations } from './general-settings.constants';
import { StyledButtonContainer } from './general-settings.styles';
import { RoomDetail } from './components/room-settings-section/room-settings-section.types';
export const GeneralSettings = ({
  roomDetails,
  isContentDev
}: GeneralSettingsProps) => {
  const {
    roomCode
  } = useParams();
  const navigate = useNavigate();
  const [updateRoom, {
    isLoading
  }] = useUpdateRoomMutation();
  const [openUpdateModal, setOpenUpdateModal] = React.useState<boolean>(false);
  const defaultValues = React.useMemo(() => ({
    ...roomDetails,
    join: getWhoCanJoinRoomValue(roomDetails?.freeToUse, roomDetails?.businessOnly)
  }), [roomDetails]);
  const formMethods = useForm({
    resolver: yupResolver(updateRoomValidations),
    defaultValues,
    mode: 'onChange'
  });
  const {
    formState: {
      isDirty,
      dirtyFields,
      errors
    },
    reset,
    handleSubmit
  } = formMethods;
  const isRoomCodeChanged = Boolean(dirtyFields?.code);
  const handleDiscardChanges = () => {
    reset();
  };
  React.useEffect(() => {
    store.dispatch(setIsDirty(isDirty));
  }, [isDirty]);
  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);
  const onSubmit = async (values: RoomDetail) => {
    try {
      const {
        title,
        description,
        type,
        public: isPublic,
        cloneable,
        difficulty,
        simpleRoom,
        locked,
        scoreType,
        timeToComplete,
        options,
        code,
        join,
        teams,
        teamSettings,
        userWriteup,
        roomCreatorType,
        networkId
      } = values;
      let tags = values?.tags;
      if (tags && typeof tags[0] === 'string') tags = [];
      if (type === 'challenge' && isPublic && !userWriteup) {
        NotificationDispatch('error', 'Uh-oh! Please add an official writeup before requesting to go public.');
        return;
      }
      const {
        freeToUse,
        businessOnly
      } = transformPermissionFields(join);
      const updatedPayload: UpdatedRoomPayload = {
        title,
        description,
        tags,
        type,
        isPublic,
        cloneable,
        difficulty,
        freeToUse,
        simpleRoom: Boolean(simpleRoom),
        locked: Boolean(locked),
        scoreType,
        timeToComplete,
        attackBoxBtn: Boolean(options?.remAttackBoxBtn),
        isResetProgressEnabled: Boolean(options?.isResetProgressEnabled),
        code,
        teams,
        teamSettings,
        businessOnly,
        roomCreatorType,
        networkId
      };
      await updateRoom({
        roomCode: (roomCode as string),
        payload: updatedPayload
      }).unwrap();
      reset();
      setTimeout(() => {
        if (isRoomCodeChanged) {
          navigate(`/room/manage/${code}`, {
            replace: true
          });
        }
      }, 1000);
      NotificationDispatch('success', 'Room updated successfully.');
    } catch (error: unknown) {
      if (isQueryErrorWithMessage(error)) {
        displayErrors(error);
      } else {
        NotificationDispatch('error', 'Uh-oh. There was an error while updating your room. Please try again.');
      }
    } finally {
      setOpenUpdateModal(false);
    }
  };
  return <>
      <FormProvider {...formMethods} data-sentry-element="FormProvider" data-sentry-source-file="general-settings.tsx">
        <GeneralSection data-sentry-element="GeneralSection" data-sentry-source-file="general-settings.tsx" />
        <RoomSettingsSection roomDetails={roomDetails} isContentDev={isContentDev} data-sentry-element="RoomSettingsSection" data-sentry-source-file="general-settings.tsx" />

        <StyledButtonContainer data-sentry-element="StyledButtonContainer" data-sentry-source-file="general-settings.tsx">
          {isDirty && <DiscardChangesModal handleActionButton={handleDiscardChanges} />}
          <UpdateRoomConfirmModal open={openUpdateModal} openChange={setOpenUpdateModal} isRoomCodeChanged={isRoomCodeChanged} isUpdatingRoom={isLoading} isUpdateButtonDisabled={!isDirty || Object.keys(errors).length > 0}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        handleUpdateRoom={handleSubmit(onSubmit)} data-sentry-element="UpdateRoomConfirmModal" data-sentry-source-file="general-settings.tsx" />
        </StyledButtonContainer>
      </FormProvider>

      <UnSavedWarnModal isSavingChanges={isLoading} isRoomCodeFieldChanged={isRoomCodeChanged} handleDiscardChanges={handleDiscardChanges}
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleActionButton={handleSubmit(onSubmit)} data-sentry-element="UnSavedWarnModal" data-sentry-source-file="general-settings.tsx" />
    </>;
};